// Import necessary React hooks
import { useState } from "react";
// Import necessary Material-UI components
import { Button, Grid, Tooltip, Typography } from "@mui/material";
// Import utility functions and custom components
import { checkKnownDateOrString, LoadingButtonText } from "../../common";
import { EditSampleRequestDetailsModal, AddItemSampleRequestModal } from "./";
import { useNewRequestContext } from "../hooks";
// Import lodash utility functions
import _ from "lodash";

/**
 * SampleRequestDetails component showing all details of the sample request
 * @param {Object} props - Component props including data, and callbacks.
 */
export function SampleRequestDetails({ handleSubmit, isSubmitting }) {
  // State to control the visibility of the EditSampleRequestDetailsModal
  const [openEditRequestModal, setOpenEditRequestModal] = useState(false);
  // Function to open the EditSampleRequestDetailsModal
  const handleOpenEditRequestModal = () => setOpenEditRequestModal(true);
  // State to control the visibility of the AddItemSampleRequestModal
  const [openAddItemModal, setOpenAddItemModal] = useState(false);
  // Function to open the AddItemSampleRequestModal
  const handleOpenAddItemModal = () => setOpenAddItemModal(true);
  // Access the current state of the new request using the custom hook
  const { state } = useNewRequestContext();

  // Create a new object from state.sampleRequestDetails, omitting the 'items' property
  let newData = _.omit(state.sampleRequestDetails, ["items"]);

  return (
    <Grid item>
      <div className="card">
        <div className="card__text" {...TextDivProps}>
          {/*  Render the title and instructions for the sample request details */}
          <Grid item xs={12} mb={3}>
            <Typography component="h1" variant="h6">
              The list of items in the sample request is given below. Click on
              the items in the list to edit or delete the item
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              <b>Sample Request Details</b>
            </Typography>
          </Grid>
          {/*  Render the details of the sample request */}
          <Grid container columnSpacing={1}>
            {Object.keys(newData).map((oneKey, i) => {
              return (
                <Grid item xs={12} sm={6} md={4} lg={2} key={i}>
                  <Grid item>
                    <Typography>
                      {_.startCase(Object.keys(newData)[i]) + ":"}
                    </Typography>
                  </Grid>

                  <Grid item>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 1.5,
                        color: "black",
                      }}
                    >
                      {checkKnownDateOrString(oneKey, newData[oneKey])}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
          {/*  Render buttons for editing and submitting the sample request */}
          <Grid {...ButtonGridProps}>
            <Grid item xs={12} sm={4} md={2.5} lg={1.5}>
              <Button {...EditButtonProps} onClick={handleOpenEditRequestModal}>
                Edit Details
              </Button>
            </Grid>
            <Grid item xs={12} sm={4} md={2.5} lg={1.5}>
              <Tooltip
                title={
                  state.sampleRequestDetails.items.length < 1 ? (
                    <p style={{ fontSize: "13px" }}>
                      Minimum 1 item required for submission
                    </p>
                  ) : (
                    ""
                  )
                }
                placement="top"
                arrow
              >
                <div>
                  <Button
                    disabled={
                      isSubmitting ||
                      state.sampleRequestDetails.items.length < 1
                        ? true
                        : false
                    }
                    onClick={handleSubmit}
                    {...SubmitButtonProps}
                  >
                    <LoadingButtonText loading={isSubmitting} text="Submit" />
                  </Button>
                </div>
              </Tooltip>
            </Grid>
          </Grid>
        </div>
      </div>
      {/* Render the EditSampleRequestDetailsModal and AddItemSampleRequestModal components */}
      <EditSampleRequestDetailsModal
        open={openEditRequestModal}
        setOpen={setOpenEditRequestModal}
        handleOpen={handleOpenEditRequestModal}
      />
      <AddItemSampleRequestModal
        open={openAddItemModal}
        setOpen={setOpenAddItemModal}
        handleOpen={handleOpenAddItemModal}
      />
    </Grid>
  );
}

const TextDivProps = {
  style: {
    padding: "20px 20px",
    overflowWrap: "break-word",
  },
};
const ButtonGridProps = {
  container: true,
  justifyContent: "right",
  columnSpacing: 2,
  rowSpacing: 2,
};
const EditButtonProps = {
  float: "right",
  size: "small",
  variant: "contained",
  sx: {
    width: "100%",
    fontSize: "14px",
    textTransform: "capitalize",
  },
};
const SubmitButtonProps = {
  float: "right",
  size: "small",
  variant: "contained",
  sx: {
    width: "100%",
    fontSize: "14px",
    backgroundColor: "#bc9b6a",
    "&:hover": {
      backgroundColor: "#a6885b",
    },
    textTransform: "capitalize",
  },
};
